import React, { useMemo } from 'react';
import { CheckboxGroup as RACCheckboxGroup } from 'react-aria-components';

import { Size } from '../../types';
import {
  FieldLabel,
  FieldMessage,
  focusTarget,
  resolveFieldState,
  useFieldLayout,
  useFieldMessageIds,
} from '../field/field-shell';
import { CheckboxGroupStyleContext } from './group-context';

/**
 * ComplyKit CheckboxGroup: React Aria Components CheckboxGroup + Tailwind v4.
 * Props are fully abstracted from the underlying library.
 *
 * Manages the selection of the ComplyKit Checkboxes rendered inside it; each
 * child Checkbox identifies itself through its `value` prop and reads the
 * group state from context, so it needs no isSelected/onChange of its own.
 * Its label and message line are a field's (field.md), and the enclosing
 * Form decides whether the label sits above or beside the choices.
 */
interface CheckboxGroupCommonProps {
  /** The Checkboxes of the group */
  children?: React.ReactNode;
  /**
   * Help under the group: the rule the selection must meet, or why the group
   * is read-only. Inline content only. Hidden while `error` shows.
   */
  description?: React.ReactNode;
  /**
   * The error message. The group is invalid exactly when this is non-empty.
   * Say what to do, restating the rule: "Choose at least one channel".
   */
  error?: string;
  /** The values of the selected checkboxes (controlled). */
  value?: string[];
  /** The values selected on mount (uncontrolled). */
  defaultValue?: string[];
  /** Called with the selected values on every change. */
  onChange?: (value: string[]) => void;
  /** Called when focus leaves the group. */
  onBlur?: (e: React.FocusEvent) => void;
  /** Called when focus enters the group. */
  onFocus?: (e: React.FocusEvent) => void;
  /**
   * Washed out and skipped by Tab. Prefer `isReadOnly` whenever the value
   * still matters to the reader.
   */
  isDisabled?: boolean;
  /**
   * Focusable but not changeable, the marks in neutral. Say why in
   * `description`.
   */
  isReadOnly?: boolean;
  /**
   * Marks the label with an asterisk. A group carries no `aria-required`, so
   * here the mark is read by assistive technology.
   */
  isRequired?: boolean;
  /** The form field name. */
  name?: string;
  /** The id of the group element. */
  id?: string;
  /** Size inherited by the child Checkboxes (their own size prop wins). */
  size?: Size;
  /** Variant inherited by the child Checkboxes (their own variant prop wins). */
  variant?: 'solid' | 'soft' | 'outline';
}

/* The children name the choices, never the group, so a group needs exactly
 * one name of its own, held by the type as RadioGroup's is. */
interface CheckboxGroupLabelledProps extends CheckboxGroupCommonProps {
  /** The visible name, above the choices or beside them (Form decides). */
  label: string;
  'aria-label'?: never;
  'aria-labelledby'?: never;
}

interface CheckboxGroupUnlabelledProps extends CheckboxGroupCommonProps {
  label?: never;
  /** The accessible name of a group with no visible label. */
  'aria-label': string;
  'aria-labelledby'?: never;
}

interface CheckboxGroupLabelledByProps extends CheckboxGroupCommonProps {
  label?: never;
  'aria-label'?: never;
  /** The id of the visible element that names the group (a question's text). */
  'aria-labelledby': string;
}

export type CheckboxGroupProps =
  | CheckboxGroupLabelledProps
  | CheckboxGroupUnlabelledProps
  | CheckboxGroupLabelledByProps;

/**
 * The forwarded ref is the first checkbox a keyboard can reach, so a form
 * library focusing the first invalid field lands on a real input.
 */
export const CheckboxGroup = React.forwardRef<HTMLInputElement, CheckboxGroupProps>(
  function CheckboxGroup(
    {
      children,
      label,
      description,
      error: errorProp,
      isRequired: requiredProp,
      isDisabled: disabledProp,
      isReadOnly: readOnlyProp,
      size = 'md',
      variant,
      ...props
    },
    ref,
  ) {
    const rootRef = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(ref, () => focusTarget(rootRef.current) as HTMLInputElement);
    const { error, isRequired, isDisabled, isReadOnly, isInvalid } = resolveFieldState({
      error: errorProp,
      isRequired: requiredProp,
      isDisabled: disabledProp,
      isReadOnly: readOnlyProp,
    });
    const inherited = useMemo(
      () => ({ size, variant, isReadOnly, inGroup: true }),
      [size, variant, isReadOnly],
    );
    const { labelPosition, styles } = useFieldLayout(size);
    const { root, label: labelClass, column } = styles;
    const { descriptionId, errorId, describedBy } = useFieldMessageIds(description, error);

    return (
      <RACCheckboxGroup
        {...props}
        ref={rootRef}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        isInvalid={isInvalid}
        validationBehavior="aria"
        aria-describedby={describedBy}
        className={root()}
        data-component="checkbox-group"
        data-label-position={labelPosition}
        data-required={isRequired || undefined}
      >
        {label ? (
          <FieldLabel isRequired={isRequired} announcesRequired className={labelClass()}>
            {label}
          </FieldLabel>
        ) : null}
        <div className={column()}>
          <CheckboxGroupStyleContext.Provider value={inherited}>
            {children}
          </CheckboxGroupStyleContext.Provider>
          <FieldMessage
            description={description}
            error={error}
            descriptionId={descriptionId}
            errorId={errorId}
          />
        </div>
      </RACCheckboxGroup>
    );
  },
);
